<template>
  <div class="container-fluid">
    <div class="row">
      <b-modal size="md" hide-footer ref="FinancesCompensationsModal">
        <template v-slot:modal-header="{ cancel }">
          <h5 class="modal-title">
            {{ !!editedItem.id ? "Edit compensation" : "Create compensation" }}
          </h5>
          <button
            type="button"
            aria-label="Close"
            class="close"
            @click.prevent="cancel"
          >
            <span class="material-icons">
              close
            </span>
          </button>
        </template>
        <FinancesCompensationsForm
          v-if="dialog"
          :dataProps="editedItem"
          :allUsers="getAllUsers"
          :currencies="currencies"
          :taxTypes="taxTypes"
          :edit="!!editedItem.id"
          :modal="true"
          :isLoading="isLoading"
          :show-preloader="true"
          @submit="submit"
        />
      </b-modal>

      <div class="col-12">
        <div class="page-table-header">
          <div class="title-container">
            <h3 class="title log_time_head">
              Compensations
              <div
                v-if="loading"
                class="spinner-border spinner-theme_default"
                role="status"
              >
                <span class="sr-only">Loading...</span>
              </div>
            </h3>
            <div class="title-container__controls">
              <b-button
                class="button title-container__controls-item"
                :variant="isShowFilter ? 'secondary' : 'info'"
                @click="toggleShowFilter"
              >
                <span class="material-icons icon-image-preview">
                  tune
                </span>
                Filters
              </b-button>
              <b-button
                v-if="$can('create-finances_compensations')"
                variant="success"
                class="button"
                @click="editEntityModalOpen()"
                :disabled="loading"
              >
                Create
              </b-button>
            </div>
          </div>

          <Filters
            v-show="isShowFilter"
            textButtonSubmit="Filter"
            :data="getTableFilters"
            :isLoading="loading"
            :data-changes="true"
            @changes="checkChanges"
            @submit="setFilterData"
          />

          <TableForPage
            :tableData="getUsersData"
            :fields="table.fields"
            :slots="table.slots"
            :actions="table.actions"
            :sticky-header="false"
            @edit="editEntityModalOpen"
            @remove="deleteEntity"
          >
            <template slot="user" slot-scope="data">
              <TableUserItem :user="data.item.user" />
            </template>
            <template v-slot:comment="data">
              <div class="user-comment__list p-0">
                <TextEditor
                  :show-text="true"
                  data-already-loaded
                  :entity-type="commentEntityTypes.UserCompensations"
                  :entity-id="data.item.user.id"
                />
              </div>
            </template>
            <template slot="compensation" slot-scope="data">
              <multiselect
                v-model="form.compensations[data.item.user.id]"
                :options="data.item.compensations"
                track-by="id"
                label="label"
                :allow-empty="false"
                :showLabels="false"
              />
            </template>
          </TableForPage>
          <error-modal ref="errorModal" />
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { commentEntityTypes } from '@mocks/comment-entity-types'
import Moment from 'moment'
import { mapActions, mapGetters } from 'vuex'
import FinancesCompensationsForm from '../components/forms/FinancesCompensationsForm'
import FinancesMixin from '../mixins/finances'
import FrontFilterMixin from '../mixins/FrontFilterMixin'
import ErrorModal from '../components/modals/ErrorModal.vue'
import TableForPage from '@tables/TableForPage'
import { getDateFrom } from '../functions/format_date'
import Filters from '@components/Filter/Filter'
import TableUserItem from '@components/tables/TableUserItem'
import TextEditor from '@components/TextEditor'

export default {
  name: 'FinancesCompensations',
  components: {
    ErrorModal,
    FinancesCompensationsForm,
    TableForPage,
    TableUserItem,
    Filters,
    TextEditor
  },
  mixins: [FinancesMixin, FrontFilterMixin],
  data () {
    return {
      loading: true,
      dialog: false,
      defaultFormData: {},
      form: {
        compensations: []
      },
      editedItem: {},
      filteredUsers: [],
      table: {
        fields: [
          {
            key: 'user',
            label: 'Specialist',
            sortable: true
          },
          {
            key: 'compensation',
            label: 'Compensation'
          },
          {
            key: 'comment',
            label: 'Comment'
          },
          {
            key: 'actions',
            label: 'Actions',
            class: 'column_action'
          }
        ],
        slots: {
          user: {
            name: 'user'
          },
          comment: {
            name: 'comment'
          },
          compensation: {
            name: 'compensation'
          }
        },
        data: [],
        totalsData: [],
        actions: [
          { label: 'Edit', event: 'edit', show: this.$can('update_own-finances_compensations') },
          { label: 'Delete', event: 'remove', show: this.$can('delete_own-finances_compensations') }
        ]
      },
      filter: {
        fields: {
          'users': {
            type: 'multiselect',
            name: 'users',
            label: 'Select user',
            class: 'col-md-3',
            valueField: 'jira_user_name',
            selectLabel: 'label',
            options: [],
            value: []
          },
          'user_status': {
            type: 'select',
            name: 'user_status',
            label: 'Select status',
            class: 'col-md-2',
            valueField: 'status',
            selectLabel: 'label',
            options: [{ status: 'all', label: 'All' }, { status: 'active', label: 'Active' }, { status: 'inactive', label: 'Inactive' }],
            value: null
          },
          'users_roles': {
            type: 'multiselect',
            name: 'users_roles',
            label: 'Select role',
            class: 'col-md-2',
            valueField: 'name',
            selectLabel: 'display_name',
            options: [],
            value: []
          },
          'employment_type': {
            type: 'select',
            name: 'employment_type',
            label: 'Select employment type',
            class: 'col-md-2',
            valueField: 'type',
            selectLabel: 'label',
            options: [
              { type: 'all', label: 'All empl. types' },
              { type: 'team_member', label: 'Team member' },
              { type: 'independent', label: 'Independent' },
              { type: 'partner', label: 'Partner' }
            ],
            value: { type: 'all', label: 'All empl. types' }
          },
          'partners': {
            type: 'multiselect',
            name: 'partners',
            label: 'Select partner',
            class: 'col-md-3',
            valueField: 'id',
            selectLabel: 'company_name',
            options: [],
            value: [],
            hidden: true
          }
        }
      }
    }
  },
  created () {
    (async () => {
      this.loading = true
      await this.getEntities()
      await this.fetchComments({ type: commentEntityTypes.UserCompensations })
      Promise.all([
        this.$api.users.getAllUsers(),
        this.$api.partners.getAllPartners(),
        this.$api.finances.getCurrencies(),
        this.$api.finances.getTaxTypes(),
        this.fetchAllProjects(),
        this.$api.projects.getJiraProjects()
      ])
        .then(response => {
          this.setAllOptionsFilter()
        })
        .catch(e => {})
        .finally(() => {
          this.loading = false
        })
    })()
  },
  computed: {
    ...mapGetters({
      dateFormat: 'dateFormat',
      getProjects: 'projects/getProjects',
      taxTypes: 'finances/getTaxTypes',
      getAllUsers: 'getAllUsersWithTrashed',
      getUserRoles: 'getRoles',
      getAllPartners: 'partners/getAllPartners'
    }),
    isLoading () {
      return this.loading
    },
    getTableFilters () {
      return this.filter.fields || {}
    },
    currencies () {
      return this.$store.getters.getCurrencies.filter(currency => ['USD', 'UAH'].includes(currency.code)) || []
    },
    getUsersData () {
      if (!this.filteredUsers.length && !this.filterState) {
        return this.table.data
      }
      return this.filteredUsers || []
    },
    commentEntityTypes () {
      return commentEntityTypes
    }
  },
  watch: {
    'filter.date_from': function (val) {
      if (
        Moment(val, 'YYYY-MM-DD').isAfter(this.filter.date_to, 'YYYY-MM-DD')
      ) {
        this.filter.date_to = this.filter.date_from
      }
    }
  },
  methods: {
    ...mapActions({
      fetchComments: 'comments/fetchComments'
    }),
    async fetchAllProjects () {
      if (this.getProjects.length) return

      await this.$api.projects.getProjects()
    },
    setAllOptionsFilter () {
      this.filter.fields.users.options = this.getAllUsers
      this.filter.fields.users_roles.options = [
        {
          display_name: 'All',
          name: 'all'
        },
        ...this.getUserRoles
      ]
      this.filter.fields.partners.options = [
        {
          company_name: 'All partners',
          name: 'All'
        },
        ...this.getAllPartners // for example
      ]
    },
    getDate (date, target) {
      let result = getDateFrom(date, target)
      if (target === 'from') {
        this.filter.date_from = result
      } else {
        this.filter.date_to = result
      }
    },
    getEntities () {
      this.loading = true
      this.axios
        .get('/api/finances/compensations', {
          headers: {
            Authorization: 'Bearer' + localStorage.getItem('access_token')
          }
        })
        .then(response => {
          let compensationsData = response.data
          this.table.data = compensationsData
          this.setFormCompensations()
          // let formCompensations = [];
          // compensationsData.forEach(function (item, index) {
          //     formCompensations[item.user.id] = item.compensations.find(
          //         function(compensation) {
          //             return !!compensation.is_actual_value;
          //         }
          //     );
          //     formCompensations[item.user.id].user = item.user;
          // });
          //
          // this.form.compensations = formCompensations;
        })
        .catch(error => {
          const errors = Object.values(error.response.data.errors)
          for (var i = 0; i < errors.length; i++) {
            this.$snotify.error(
              errors[i][0],
              'Error',
              this.$store.state.snotify
            )
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    setFormCompensations () {
      let formCompensations = []
      let compensationsData = this.table.data
      compensationsData.forEach(function (item, index) {
        formCompensations[item.user.id] = item.compensations.find(function (
          compensation
        ) {
          return !!compensation.is_actual_value
        })

        if (formCompensations[item.user.id] === undefined) {
          formCompensations[item.user.id] = item.compensations[0]
        }
        formCompensations[item.user.id].user = item.user
      })

      this.form.compensations = formCompensations
    },
    create (data, files) {
      this.loading = true

      this.axios
        .post('/api/finances/compensations', data, {
          headers: {
            Authorization: 'Bearer' + localStorage.getItem('access_token')
          }
        })
        .then(({ data }) => {
          this.$snotify.success(
            'Compensation successfully created',
            'Success',
            this.$store.state.snotify
          )

          this.dialog = false
          this.getEntities()
          this.editedItem = {}
          this.$refs['FinancesCompensationsModal'].hide()
        })
        .catch(error => {
          const errors = Object.values(error.response.data.errors)
          for (var i = 0; i < errors.length; i++) {
            this.$snotify.error(
              errors[i][0],
              'Error',
              this.$store.state.snotify
            )
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    editEntityModalOpen (item) {
      let data = {}
      let additionalData = {}

      if (item) {
        data = this.form.compensations[item.user.id]
        additionalData = { user: item.user }
      }

      this.editedItem = Object.assign(Object.assign({}, data), additionalData)

      this.dialog = true
      this.$refs['FinancesCompensationsModal'].show()
    },
    edit (data) {
      this.loading = true
      data['_method'] = 'PUT'
      this.axios
        .post('/api/finances/compensations/' + this.editedItem.id, data, {
          headers: {
            Authorization: 'Bearer' + localStorage.getItem('access_token')
          }
        })
        .then(({ data }) => {
          this.$snotify.success(
            'Compensation successfully saved',
            'Success',
            this.$store.state.snotify
          )

          this.dialog = false
          this.getEntities()
          this.editedItem = {}
          this.$refs['FinancesCompensationsModal'].hide()
        })
        .catch(error => {
          const errors = Object.values(error.response.data.errors)
          for (var i = 0; i < errors.length; i++) {
            this.$snotify.error(
              errors[i][0],
              'Error',
              this.$store.state.snotify
            )
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    submit (data, files) {
      if (this.editedItem.id) {
        this.edit(data, files)
      } else {
        this.create(data, files)
      }
    },
    async deleteEntity (item) {
      const confirm = await this.$root.$confirm.open(
        'Delete',
        'Are you sure you want to delete the compensation?'
      )

      if (!confirm) return

      const id = this.form.compensations[item.user.id].id

      this.loading = true
      this.axios
        .delete('/api/finances/compensations/' + id, {
          headers: {
            Authorization: 'Bearer' + localStorage.getItem('access_token')
          }
        })
        .finally(() => {
          this.getEntities()
        })
    },
    setFilterData (data) {
      const filterByUser = data.users ? data.users.value : []
      const filterByUserStatus = data.user_status ? data.user_status.value : null
      const filterByUserRole = data.users_roles ? data.users_roles.value : []
      const filterByEmplType = data.employment_type ? data.employment_type.value : null
      const filterByPartners = data.partners ? data.partners.value : []

      let users = [...this.table.data]

      this.checkFilters(data)

      if (filterByUser.length) {
        users = this.usersFilter(users, filterByUser)
      }

      if (filterByUserStatus) {
        users = this.statusFilter(users, filterByUserStatus)
      }

      if (filterByUserRole.length) {
        users = this.rolesFilter(users, filterByUserRole)
      }

      if (filterByEmplType) {
        users = this.emplTypeFilter(users, filterByEmplType)
      }

      if (filterByPartners.length) {
        users = this.partnersFilter(users, filterByPartners)
      }

      this.filteredUsers = users
    }
  },
  updated () {}
}
</script>

<style scoped>
.table-wrapper {
  padding-top: 20px;
}
.table-wrapper h3 {
  margin-bottom: 30px;
}
.entities-table {
  background-color: #fff;
}
/*.entities-table-wrapper {*/
/*  max-height: 600px;*/
/*  overflow-y: auto;*/
/*}*/
.spinner-border {
  border-width: 0.25rem !important;
}
.filter-buttons {
  padding-top: 34px;
}
</style>
<style lang="scss">
  .table__theme-default > div.table-container {
    overflow: visible;
  }
</style>
